import React, { FC, ReactNode, useEffect, useState } from 'react';
import { TablesNames } from '../../store/tables/tables-context';
import { getFormattedBatchesData, IBatchesData } from '../../interfaces/funds';
import { Table } from '../table/Table';
import { EXPORT_NAME } from '../../views/batches/BatchesListPage';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment, faDownload } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { usePaymentContext } from '../../store/payments/payments-context';
import { IModalWindow, MODAL_TYPE, ModalWindow } from '../modal/ModalWindow';
import { batchAction, setComment, setInternalComment } from '../../store/order/actions/batches';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { fetchUnpostedBatchData, setBatchesDataToDefault } from '../../store/payments/actions/batches';
import { useLocation } from 'react-router';
import { parseSearchParams } from '../../utils/utilities';
import { IBatchesFiltersParams } from './BatchesFilterSection';
import { BATCH_ACTIONS } from './BatchDetailsInformationHeader';
import { exportCSVBatchesData } from '../../store/order/actions/exports';
import { EmptyBatches } from './EmptyBatches';
import { ExportCSVButton } from '../form/ExportCSVButton';
import { IDropdownItems } from '../disputes/disputes_queues/disputes_queue_details/DisputeQueuesTable';
import { MenuItem } from '@mui/material';
import { CommentModal } from './CommentComponent';
import { COMMENT_FORM_FIELD, COMMENT_FORM_FIELD_NAME } from '../../constants/comment/comment-form';
import { checkRoles, CLAIMS } from '../../utils/roles-helper';
import { IAuthState } from '../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';

enum BATCHES_ACTION_NAMES {
  POST = 'Post',
  SHELVE = 'Shelve',
  COMMENT = 'Public comment',
  INTERNAL_COMMENT = 'Internal comment',
}

interface Props {
  listener?: (search: any) => Promise<void>;
}
export const UnpostedBatchesList: FC<Props> = ({ listener }) => {

  const { t } = useTranslation();
  const { search } = useLocation();
  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);

  const { merchantState: { isListOfMerchantsLoading, isPartnerNamesListLoading }} = useMerchants();
  const { paymentContextState, dispatch } = usePaymentContext();

  const { unpostedBatchesData, isUnpostedBatchesDataLoading } = paymentContextState;
  const [isUnpostedBatchesExportLoading, setIsUnpostedBatchesExportLoading] = useState<boolean>(false);

  const [modalState, setModalState] = useState<IModalWindow>({
    isOpen: false
  });
  const [batchModalState, setBatchModalState] = useState({
    isOpen: false,
    batchId: '',
    action: null,
  });

  const filtersFromUrl: IBatchesFiltersParams = parseSearchParams(search) || {};

  const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

  const closeBatchModal = (): void => setBatchModalState({ isOpen: false, batchId: '', action: null });

  useEffect(() => {
    return () => {
      setBatchesDataToDefault(dispatch);
    };
  }, []);

  const fetchExportData = async (exportName: string): Promise<void> => {
    setIsUnpostedBatchesExportLoading(true);

    await exportCSVBatchesData({ exportName, params: filtersFromUrl });

    setIsUnpostedBatchesExportLoading(false);
  };


  const shelveSuccessModalAction = async (): Promise<void> => {
    closeModal();
    await fetchUnpostedBatchData({ dispatch, params: filtersFromUrl });
  };

  const handleShelveSuccess = (): void => {
    closeBatchModal();
    setModalState({
      isOpen: true,
      modalType: MODAL_TYPE.APPROVE,
      modalTitle: t('PENDING_PAYOUTS.SHELVE_MODAL_TITLE'),
      modalText: 'The batch has been successfully moved to the Shelved batches list.',
      onClose: shelveSuccessModalAction,
      confirmButtonText: 'Ok',
      onConfirm: shelveSuccessModalAction,
    });
  };

  const openCommentModal = (data: IBatchesData, isInternalComments?: boolean): void => {
    setModalState({
      isOpen: true,
      modalTitle: isInternalComments ? t('PENDING_PAYOUTS.INTERNAL_COMMENT_MODAL_TITLE') : t('PENDING_PAYOUTS.PUBLIC_COMMENT_MODAL_TITLE'),
      contentClassName: 'd-block',
      minHeight: 100,
      onClose: closeModal,
      content: <CommentModal
        fieldName={isInternalComments ? COMMENT_FORM_FIELD.INTERNAL_COMMENT : COMMENT_FORM_FIELD.COMMENT}
        label={t('PENDING_PAYOUTS.COMMENT_MODAL_TEXT')}
        placeholder={t(COMMENT_FORM_FIELD_NAME.COMMENT_PLACEHOLDER)}
        value={isInternalComments ? data?.internalComment : data?.comment}
        payoutId={data?.payoutId}
        closeModal={closeModal}
        additionalClass="col-12 col-md-12"
        additionalContentClass="px-8 pb-8"
        saveComment={async (payoutId, comment): Promise<void> => {
          return isInternalComments
            ? setInternalComment(payoutId, comment)
            : setComment(payoutId, comment);
        }}
      />,

    });
  };

  const dropDownItems = [
    {
      name: BATCHES_ACTION_NAMES.SHELVE,
      picture: faDownload,
      action: ({ id }): void => setBatchModalState({
        isOpen: true,
        batchId: id,
        action: BATCH_ACTIONS.SHELVE
      }),
      claims: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN,
    },
    {
      name: BATCHES_ACTION_NAMES.COMMENT,
      picture: faComment,
      action: ({ data }): void => openCommentModal(data),
      claims: CLAIMS.SUPER_ADMIN_AND_PARTNER_ADMIN,
    },
    {
      name: BATCHES_ACTION_NAMES.INTERNAL_COMMENT,
      picture: faComment,
      action: ({ data }): void => openCommentModal(data, true),
      claims: CLAIMS.SUPER_ADMIN,
    },
  ];

  const rowActionMenuItems = (rowData, closeMenu): ReactNode[] => {
    const filteredDropdownItems = dropDownItems
      .filter((item) => checkRoles(authUser, item.claims));

    return filteredDropdownItems?.map(({ name, picture, action }: IDropdownItems) => {
      return (
        <MenuItem
          key={name}
          onClick={(): void => {
            action({
              id: rowData?.original?.id,
              data: rowData?.original,
            });
            closeMenu();
          }}
          className={clsx('fw-bolder fs-7 main-font-family text-muted')}
        >
          <FontAwesomeIcon icon={picture} className="me-1"/>
          {name}
        </MenuItem>
      );
    });
  };

  if (!search) {
    return <EmptyBatches />;
  }

  return (
    <div>
      <Table
        name={TablesNames.unpostedBatchesTableData}
        data={getFormattedBatchesData(unpostedBatchesData) ?? []}
        isLoading={isUnpostedBatchesDataLoading || isListOfMerchantsLoading || isPartnerNamesListLoading}
        enableRowActions={true}
        rowActionMenuItems={rowActionMenuItems}
        actionColumnAlign="left"
        actionColumnsSize={50}
        queryParamsListener={listener}
        positionActionsColumn="last"
        toolbarCustomAction={
          <>
            <ExportCSVButton
              orderTotalItemCount={unpostedBatchesData?.length ?? 0}
              isExportCSVLoading={isUnpostedBatchesExportLoading}
              exportCSV={async (): Promise<void> => await fetchExportData(EXPORT_NAME.UNPOSTED_PAYOUT)}
            />
          </>
        }
      />

      <ModalWindow
        {...modalState}
      />

      <ModalWindow
        isOpen={batchModalState?.action === BATCH_ACTIONS.SHELVE && batchModalState.isOpen}
        modalType={MODAL_TYPE.SAVE}
        modalTitle={t('PENDING_PAYOUTS.SHELVE_MODAL_TITLE')}
        modalText={t('PENDING_PAYOUTS.SHELVE_MODAL_TEXT')}
        backdrop="static"
        onCancel={closeBatchModal}
        onClose={closeBatchModal}
        onConfirm={(): Promise<void> => batchAction({
          batchId: batchModalState.batchId,
          action: batchModalState?.action,
          onSuccess: handleShelveSuccess,
          onClose: closeBatchModal,
        })}
      />
    </div>
  );
};
